import { useSuspenseQuery } from "@apollo/client/react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useShowValidationErrors } from "~/common/hooks";
import type { MutationRefetchQueries } from "~/common/types";
import { Button, Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Input } from "~/components/ui";
import { PropertySchemaAssociationsList, usePropertySchemaAssociationsForm } from "~/features/admin/propertySchemas";
import {
  GetEvidenceSetDetailDocument,
  type GetEvidenceSetDetailQuery,
  type GetEvidenceSetDetailQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { useEvidenceSetEdit } from "~/features/dataImportProjects/hooks/useEvidenceSetEdit";
import {
  EvidencePropertySchemaTypes,
  type EvidenceSetEditFormData,
  zEvidenceSetEditFormDataKeys,
} from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";

export const EVIDENCE_SET_EDIT_FORM_ID = "evidence-set-edit-form";

interface EvidenceSetEditFormProps {
  onSubmit: () => void;
  evidenceSetId: string;
  refetchQueriesOnEdit: MutationRefetchQueries;
}

export function EvidenceSetEditForm({ onSubmit, refetchQueriesOnEdit, evidenceSetId }: EvidenceSetEditFormProps) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  const { data } = useSuspenseQuery<GetEvidenceSetDetailQuery, GetEvidenceSetDetailQueryVariables>(
    GetEvidenceSetDetailDocument,
    { variables: { tenantId, id: evidenceSetId } },
  );
  const evidenceSet = data?.evidenceSets?.[0];

  const methods = useForm<EvidenceSetEditFormData>({
    defaultValues: {
      name: evidenceSet?.name ?? "",
      propertySchemaSetId: evidenceSet?.evidenceSetPropertySchemaSet?.propertySchemaSet.id,
    },
  });
  const { handleSubmit, control, register, setError } = methods;
  const showValidationErrors = useShowValidationErrors(setError, zEvidenceSetEditFormDataKeys);

  const { submit } = useEvidenceSetEdit({
    onSubmit,
    evidenceSetId,
    refetchQueriesOnEdit,
    onError: (error) => {
      showValidationErrors(error.fields);
    },
  });

  const propertySchemaAssociations = usePropertySchemaAssociationsForm(control);

  useEffect(() => {
    const associations = evidenceSet?.evidenceSetPropertySchemaSet?.propertySchemaSet.propertySchemaAssociations ?? [];
    propertySchemaAssociations.setDefaults(associations);
  }, [data]);

  if (!evidenceSet) {
    throw new Error("Evidence set does not exist!");
  }

  return (
    <Form {...methods}>
      <form id={EVIDENCE_SET_EDIT_FORM_ID} onSubmit={handleSubmit(submit)} className="space-y-6 pt-3">
        <input type="hidden" {...register("propertySchemaSetId")} />

        <FormField
          control={control}
          name="name"
          rules={{ required: t("ui.required") }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.common.name")}</FormLabel>
              <FormControl>
                <Input value={field.value} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} required />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <PropertySchemaAssociationsList
          title={<h3 className="text-14-bold text-fg-1">{t("model.propertySchema.modelNames")}</h3>}
          fields={propertySchemaAssociations.fields}
          onRemove={propertySchemaAssociations.remove}
          onMove={propertySchemaAssociations.move}
          supportedPropertyTypes={EvidencePropertySchemaTypes}
        />

        <div className="flex justify-end">
          <Button type="button" variant="outline" onClick={propertySchemaAssociations.append}>
            {t("feature.dataImportProjects.form.addPropertySchema")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
